import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { Debug, LogCategory, LogLevel } from '../../../shared/Debug';

// Cache for loaded models to prevent reloading the same model multiple times
const modelCache = new Map<string, THREE.Group>();
const animationCache = new Map<string, THREE.AnimationClip[]>();

/**
 * Loads a GLTF/GLB model from the specified URL
 * @param url Path to the GLB/GLTF file
 * @returns Promise that resolves with the loaded model
 */
export async function loadGLTFModel(url: string): Promise<{
  model: THREE.Group;
  animations: THREE.AnimationClip[];
}> {
  Debug.info(`Loading GLB model from: ${url}`, LogCategory.GENERAL);
  
  // Check cache first
  if (modelCache.has(url) && animationCache.has(url)) {
    Debug.info(`Using cached model for: ${url}`, LogCategory.GENERAL);
    
    // Use a more efficient clone method that shares geometries and materials
    const cachedModel = modelCache.get(url)!.clone(false);
    
    // Ensure the cached model is visible
    cachedModel.visible = true;
    cachedModel.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.visible = true;
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    
    // Create proper clones of the animations to avoid shared state
    // but use direct cloning for better performance
    const cachedAnimations = animationCache.get(url)!;
    const clonedAnimations = cachedAnimations.map(anim => anim.clone());
    
    return {
      model: cachedModel,
      animations: clonedAnimations
    };
  }

  // Set up loaders
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('/assets/draco/');
  
  Debug.info('DRACOLoader decoder path set to: /assets/draco/', LogCategory.GENERAL);

  const loader = new GLTFLoader();
  loader.setDRACOLoader(dracoLoader);

  // Load the model
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        Debug.info('GLB model loaded successfully', LogCategory.GENERAL);
        
        if (!gltf.scene) {
          Debug.error('No scene found in the loaded model', LogCategory.GENERAL);
          reject(new Error('No scene found in the loaded model'));
          return;
        }
        
        // Ensure the model and all its children are visible
        gltf.scene.visible = true;
        gltf.scene.traverse(child => {
          if (child instanceof THREE.Mesh) {
            child.visible = true;
            child.castShadow = true;
            child.receiveShadow = true;
          }
        });
        
        // Log model hierarchy
        Debug.info('Model hierarchy:', LogCategory.GENERAL);
        logModelHierarchy(gltf.scene);
        
        // Log animation info
        Debug.info(`Model contains ${gltf.animations.length} animations:`, LogCategory.GENERAL);
        
        // Store the original in cache but apply shallow cloning to preserve geometry/material sharing
        const cacheModel = gltf.scene.clone(false);
        
        // Store all reusable geometries and materials separately to avoid duplicating them
        const geometryCache = new Map<string, THREE.BufferGeometry>();
        const materialCache = new Map<string, THREE.Material>();
        
        cacheModel.traverse(child => {
          if (child instanceof THREE.Mesh) {
            // Give the geometry a unique id if it doesn't have one
            if (!child.geometry.uuid) {
              child.geometry.uuid = THREE.MathUtils.generateUUID();
            }
            geometryCache.set(child.geometry.uuid, child.geometry);
            
            // Handle single material and material array cases
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => {
                if (!mat.uuid) {
                  mat.uuid = THREE.MathUtils.generateUUID();
                }
                materialCache.set(mat.uuid, mat);
              });
            } else if (child.material) {
              if (!child.material.uuid) {
                child.material.uuid = THREE.MathUtils.generateUUID();
              }
              materialCache.set(child.material.uuid, child.material);
            }
          }
        });
        
        // Store the model with its shared resources
        modelCache.set(url, cacheModel);
        
        // Store animations in cache using direct cloning for better performance
        const animClones = gltf.animations.map(anim => anim.clone());
        animationCache.set(url, animClones);
        
        // Return the original model and animations to the caller
        resolve({
          model: gltf.scene,
          animations: gltf.animations
        });
      },
      (progress) => {
        const percent = progress.total > 0 
          ? Math.round((progress.loaded / progress.total) * 100) 
          : 0;
        Debug.info(`Loading model ${url}: ${percent}%`, LogCategory.GENERAL);
      },
      (error) => {
        Debug.error('Error loading model: ' + error, LogCategory.GENERAL);
        reject(error);
      }
    );
  });
}

/**
 * Helper function to log the hierarchy of a model
 */
export function logModelHierarchy(object: THREE.Object3D, indent: string = ''): void {
  Debug.info(`${indent}${object.name || 'unnamed'} (${object.type})`, LogCategory.GENERAL);
  object.children.forEach(child => {
    logModelHierarchy(child, indent + '  ');
  });
}

/**
 * Properly disposes of a THREE.js object and all its children
 * @param object The object to dispose
 */
export function disposeObject(object: THREE.Object3D): void {
  // Process all children first (depth-first)
  const children = [...object.children];
  children.forEach(child => {
    disposeObject(child);
    object.remove(child);
  });
  
  // Handle materials and geometries
  if (object instanceof THREE.Mesh) {
    // Dispose of geometry
    if (object.geometry) {
      object.geometry.dispose();
    }
    
    // Dispose of materials (could be an array or a single material)
    if (Array.isArray(object.material)) {
      object.material.forEach(material => {
        disposeMaterial(material);
      });
    } else if (object.material) {
      disposeMaterial(object.material);
    }
  }
}

/**
 * Properly disposes of a THREE.js material and its textures
 * @param material The material to dispose
 */
function disposeMaterial(material: THREE.Material): void {
  // Dispose of textures
  Object.keys(material).forEach(prop => {
    if (!material[prop]) return;
    
    // Check if the property is a texture
    if (material[prop] instanceof THREE.Texture) {
      const texture = material[prop] as THREE.Texture;
      if (texture.image) {
        URL.revokeObjectURL(texture.image.src); // Remove object URL if used
      }
      texture.dispose();
    }
  });
  
  // Dispose of the material itself
  material.dispose();
}

/**
 * Clears the model cache and properly disposes of all resources
 */
export function clearModelCache(): void {
  // Dispose of all models in the cache
  modelCache.forEach(model => {
    disposeObject(model);
  });
  
  // Clear the caches
  modelCache.clear();
  animationCache.clear();
} 